import { DB_RESPONSE_OK, DB_RESPONSE_ERROR } from "constants/dbStatuses";
import { mergeSortedArrays } from "util/arrays";

import { db } from "./db";
import {
	Event,
	BoothConfig,
	OfflineTicket,
	ScanSettings,
	Ticket,
	UserSettings,
	Scan,
	Revocation,
	ScanMode,
	Singleton,
	Product,
	ScanType,
	LastSyncIds,
	ScannerConfiguration,
	SeasonPass,
	PassScan,
	OfflinePassScan,
} from "./entities";
import { attributeMatch, type MatchEvent } from "util/passValidation";
import * as initialSettings from "../constants/initialSettings";
import { type InsertType } from "dexie";

/*
statuses:
100 - success
200 - error
*/

const DBStatus = {
	Ok: DB_RESPONSE_OK,
	Err: DB_RESPONSE_ERROR,
} as const;

type DBResponseStatus = (typeof DBStatus)[keyof typeof DBStatus];

type DBResponseBase<T extends any> = {
	status: DBResponseStatus;
	timestamp: Date;
} & (T extends void ? {} : { data: T });

export type DBResponseOk<T extends any> = DBResponseBase<T> & {
	status: typeof DBStatus.Ok;
};

export type DBResponseErr<C = unknown> = DBResponseBase<Error & { cause: C }> & {
	status: typeof DBStatus.Err;
};

export type DBResponse<T extends any, C = unknown> = DBResponseOk<T> | DBResponseErr<C>;

export function isDbResponse<T = any, C = unknown>(obj: any): asserts obj is DBResponse<T, C> {
	if (!obj || typeof obj !== "object") {
		throw new Error("DBResponse is not an object", { cause: { obj } });
	}
	if (
		typeof obj.status !== "number" ||
		![DBStatus.Ok, DBStatus.Err].includes(obj.status) ||
		!(obj.timestamp instanceof Date)
	) {
		throw new Error("Not a valid DBResponse object", { cause: { obj } });
	}
	if (obj.status === DBStatus.Err && !(obj.data instanceof Error)) {
		throw new Error("DBResponse error data is not an Error instance", { cause: obj.data });
	}
}

export const isDbResponseOk = (obj: any): obj is DBResponseOk<(typeof obj)["data"]> => {
	try {
		isDbResponse(obj);
		return obj.status === DBStatus.Ok;
	} catch {}
	return false;
};

export const isDbResponseErr = (obj: any): obj is DBResponseErr<(typeof obj)["data"]> => {
	try {
		isDbResponse(obj);
		return obj.status === DBStatus.Err;
	} catch {}
	return false;
};

function dbResponse(status: typeof DBStatus.Ok): DBResponseOk<void>;
function dbResponse(status: typeof DBStatus.Err): DBResponseErr<void>;
function dbResponse<T = any>(status: typeof DBStatus.Ok, data: T): DBResponseOk<T>;
function dbResponse<T = any>(status: typeof DBStatus.Err, data: T): DBResponseErr<T>;
function dbResponse<T = any>(status: DBResponseStatus, data?: T): DBResponseOk<T> | DBResponseErr<T> {
	const resp = {
		status,
		timestamp: new Date(),
		...(status === DBStatus.Err && !(data instanceof Error)
			? { data: new Error("DB error", { cause: data }) }
			: data && { data }),
	};
	switch (status) {
		case DBStatus.Ok:
			return resp as DBResponseOk<T>;
		case DBStatus.Err:
			if ("data" in resp && !("stack" in (resp.data as Error))) {
				Error.captureStackTrace(resp.data, dbResponse);
			}
			console.error(resp);
			return resp as DBResponseErr<T>;
	}
	return status satisfies never;
}

export async function getOfflineTicketsAmountFromDatabase(): Promise<DBResponse<number>> {
	try {
		return dbResponse(100, await db.offlinetickets.count());
	} catch (err) {
		return dbResponse(200, err);
	}
}

export async function writeOfflineTicketToDatabase(ticketData: Omit<OfflineTicket, "id">): Promise<DBResponse<void>> {
	try {
		await db.offlinetickets.add(ticketData);
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getScanSettingsFromDatabase(
	initialValue: typeof initialSettings.initialScanSettings,
): Promise<DBResponse<ScanSettings>> {
	try {
		const data = await db.singletons.get("scanSettings");
		if (!data) {
			await db.singletons.put(initialValue, "scanSettings");
			return dbResponse(DB_RESPONSE_OK, initialValue as unknown as ScanSettings);
		}
		return dbResponse(DB_RESPONSE_OK, data as ScanSettings);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getEventsRecordsFromDatabase(): Promise<DBResponse<Event[]>> {
	try {
		const events = await db.events.toArray();
		return dbResponse(DB_RESPONSE_OK, events);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getScannedTicketsRecordsFromDatabase(): Promise<DBResponse<Ticket[]>> {
	console.error("Fetching ALL TICKETS from idb! optimize this!!");
	try {
		const [scans, offline] = await Promise.all([
			db.scans.orderBy("ticket_id").reverse().uniqueKeys(),
			db.offlinetickets.orderBy("ticket_id").uniqueKeys(),
		]);
		const uniqueScans = new Set(scans.concat(offline));
		const results = await db.tickets.where("id").anyOf(Array.from(uniqueScans)).toArray();
		return dbResponse(DB_RESPONSE_OK, results);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getBoothDataFromDatabase(): Promise<DBResponse<BoothConfig | undefined>> {
	try {
		const data = await db.singletons.get("booth");
		return dbResponse(DB_RESPONSE_OK, data && (data as BoothConfig));
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getLanguageSelectionFromDatabase(): Promise<DBResponse<UserSettings["language"]>> {
	try {
		const data = await db.singletons.get("userSettings");
		if (data) {
			return dbResponse(DB_RESPONSE_OK, (data as UserSettings).language);
		}

		// Fallback if no language has been set
		return dbResponse(DB_RESPONSE_OK, "fi" as const);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getUserSettingsFromDatabase(
	initialValue: typeof initialSettings.initialUserSettings,
): Promise<DBResponse<UserSettings>> {
	try {
		const data = await db.singletons.get("userSettings");

		if (!data) {
			await db.singletons.put(initialValue, "userSettings");
			return dbResponse(DB_RESPONSE_OK, initialValue as UserSettings);
		}

		return dbResponse(DB_RESPONSE_OK, data as UserSettings);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function modifyUserSettingRecordInDatabase<K extends keyof typeof initialSettings.initialUserSettings>(
	key: K,
	value: UserSettings[K],
): Promise<DBResponse<UserSettings>> {
	try {
		console.log("modifying user setting", key, value);
		return await db.transaction("rw", db.singletons, async () => {
			const current = await db.singletons.get("userSettings");
			const updated = { ...current, [key]: value };
			await db.singletons.put(updated, "userSettings");
			return dbResponse(DB_RESPONSE_OK, updated as UserSettings);
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function modifyScanSettingsRecordInDatabase<K extends keyof typeof initialSettings.initialScanSettings>(
	key: K,
	value: ScanSettings[K],
): Promise<DBResponse<ScanSettings>> {
	try {
		return await db.transaction("rw", db.singletons, async () => {
			const current = await db.singletons.get("scanSettings");
			const updated = { ...current, [key]: value };
			await db.singletons.put(updated, "scanSettings");
			return dbResponse(DB_RESPONSE_OK, updated as ScanSettings);
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function modifyScanModeOptionsInIndexedDb(
	values: Partial<typeof initialSettings.initialScanMode>,
): Promise<DBResponse<ScanMode>> {
	try {
		return await db.transaction("rw", db.singletons, async () => {
			const current = await db.singletons.get("scanMode");
			const updated = { ...current, ...values };
			await db.singletons.put(updated, "scanMode");
			return dbResponse(DB_RESPONSE_OK, updated as ScanMode);
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function writeTicketsToIndexedDb(
	tickets: InsertType<Ticket, never>[] = [],
): Promise<DBResponse<{ added: number }>> {
	try {
		if (tickets.length > 0) {
			await Promise.all([
				db.tickets.bulkPut(tickets),
				db.singletons.update("lastSync", { lastTicketId: tickets[tickets.length - 1].id }).then(result => {
					if (result === 0) {
						return db.singletons.put(
							{ lastTicketId: tickets[tickets.length - 1].id, lastScanId: 0 },
							"lastSync",
						);
					}
				}),
			]);
		}
		return dbResponse(DB_RESPONSE_OK, { added: tickets.length });
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getScansAmountFromDatabase(): Promise<DBResponse<number>> {
	try {
		return dbResponse(DB_RESPONSE_OK, await db.scans.count());
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getTicketsAmountFromDatabase(): Promise<DBResponse<number>> {
	try {
		return dbResponse(DB_RESPONSE_OK, await db.tickets.count());
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getTicketListFromDatabase() {
	console.error("Fetching ALL TICKETS from idb! optimize this!!");
	try {
		const tickets = await db.tickets.toArray();
		return dbResponse(100, tickets);
	} catch (err) {
		console.error("err", err);
		return dbResponse(200, err);
	}
}

export async function getUniqueScansAmountFromDatabase(): Promise<DBResponse<number>> {
	try {
		return dbResponse(100, (await db.scans.orderBy("ticket_id").uniqueKeys()).length);
	} catch (err) {
		return dbResponse(200, err);
	}
}

export async function getTicketData(
	code: string,
): Promise<DBResponse<{ history: (Scan | OfflineTicket)[]; productData: Product | undefined }>> {
	try {
		const ticket = await db.tickets.get(code);
		if (!ticket) {
			return dbResponse(DB_RESPONSE_ERROR, `Ticket with code ${code} not found`);
		}
		const [offline, synced, product] = await Promise.all([
			db.offlinetickets
				.where("[ticket_id+scanned]")
				.between([ticket.id, ""], [ticket.id + 1, ""])
				.toArray(),
			db.scans
				.where("[ticket_id+scanned]")
				.between([ticket.id, ""], [ticket.id + 1, ""])
				.toArray(),
			db.products.get(ticket.product_id),
		]);
		const totalScanHistoryForTicket = mergeSortedArrays(offline, synced, (a, b) =>
			a.scanned < b.scanned ? -1 : a.scanned > b.scanned ? 1 : 0,
		);
		return dbResponse(DB_RESPONSE_OK, {
			history: totalScanHistoryForTicket,
			productData: product,
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getBoothscanDataFromDatabase(
	code: string,
	apikey: string,
): Promise<DBResponse<{ ticket: Ticket; scanned_times: number }>> {
	try {
		const ticket = await db.tickets.get(code);
		if (!ticket) {
			return dbResponse(DB_RESPONSE_ERROR, `Ticket with code ${code} not found`);
		}
		const [offline, synced] = await Promise.all([
			db.offlinetickets.where("[scan_type+ticket_id]").equals(["booth", ticket.id]).toArray(),
			db.scans.where("[scan_type+ticket_id]").equals(["booth", ticket.id]).toArray(),
		]);

		const scanned_times = synced.concat(offline).filter(s => s.metadata?.apikey === apikey).length;
		return dbResponse(DB_RESPONSE_OK, {
			ticket,
			scanned_times,
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export type ValidationResult = {
	ticketData: Ticket;
	scanHistoryForTargetArea: (OfflineTicket | Scan)[];
	productData: Product | null;
	totalScanHistoryForTicket: (OfflineTicket | Scan)[];
	accesscontrol: Event["access_controls"][0] | null;
	validSections: string[];
	revoked?: boolean;
	revokedReason?: string;
};

export async function getTicketValidationDataFromDatabase(
	code: string,
	mode: Exclude<ScanType, "section">,
): Promise<DBResponse<ValidationResult>>;
export async function getTicketValidationDataFromDatabase(
	code: string,
	mode: ScanType,
	section: number,
): Promise<DBResponse<ValidationResult & { sectionName: string }>>;
export async function getTicketValidationDataFromDatabase(
	code: string,
	mode: ScanType,
	section: number | null = null,
): Promise<DBResponse<ValidationResult | (ValidationResult & { sectionName: string })>> {
	try {
		const ticket = await db.tickets.get(code);
		if (!ticket) {
			return dbResponse(DB_RESPONSE_ERROR, `Ticket with code ${code} not found`);
		}

		// Return all entry scans that includes entries and exits
		const scanTypes = mode === "entry" || mode === "exit" ? ["entry", "exit"] : ["section"];

		const [offline, synced, productData, event, scannerConfig, revocation] = await Promise.all([
			db.offlinetickets
				.where("[ticket_id+scanned]")
				.between([ticket.id, ""], [ticket.id + 1, ""])
				.toArray(),
			db.scans
				.where("[ticket_id+scanned]")
				.between([ticket.id, ""], [ticket.id + 1, ""])
				.toArray(),
			db.products.get(ticket.product_id),
			db.events.get(ticket.event_slug),
			db.singletons.get("configuration"),
			db.revocations.get(ticket.id),
		]);

		const totalScanHistoryForTicket = mergeSortedArrays(offline, synced, (a, b) =>
			a.scanned < b.scanned ? -1 : a.scanned > b.scanned ? 1 : 0,
		);
		const scanHistoryForTargetArea = totalScanHistoryForTicket.filter(
			scan => scanTypes.includes(scan.scan_type) && scan.section_id === section,
		);

		const resp: Partial<ValidationResult> = {
			ticketData: ticket,
			productData,
			scanHistoryForTargetArea,
			totalScanHistoryForTicket,
			revoked: !!revocation,
			revokedReason: revocation?.reason,
		};

		const configSections: Array<{ id: number; name: string; display_name?: Record<string, string>; access_control: number | null }> =
			(scannerConfig as any)?.sections || [];

		const validSections = configSections
			.filter(sec => {
				if (sec.access_control === null) return false;
				const ac = event?.access_controls.find(a => a.id === sec.access_control);
				return ac?.products.includes(ticket.product_id);
			})
			.map(sec => sec.name);

		if (section !== null && section !== undefined) {
			// Use sections from scanner config (Option A: sections stored at device level, not in event.venuemap)
			const sectionData = configSections.find(sec => sec.id === section) || null;
			const accesscontrol = sectionData
				? event?.access_controls.find(ac => ac.id === sectionData.access_control)
				: null;
			return dbResponse(DB_RESPONSE_OK, {
				...resp,
				accesscontrol,
				sectionName: sectionData ? sectionData.name : "",
				validSections,
			} as ValidationResult & { sectionName: string });
		} else {
			const venueAccessControlId = event?.venuemap?.access_control || null;
			const accesscontrol = venueAccessControlId
				? event?.access_controls.find(ac => ac.id === venueAccessControlId)
				: null;
			return dbResponse(DB_RESPONSE_OK, {
				...resp,
				accesscontrol,
				validSections,
			} as ValidationResult);
		}
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getLastIdsForTicketsApiCall(): Promise<DBResponse<LastSyncIds>> {
	try {
		const data = await db.singletons.get("lastSync");
		if (data) {
			return dbResponse(DB_RESPONSE_OK, data as LastSyncIds);
		}
		const [ticket, scan, revocation, passScan] = await Promise.all([
			db.tickets.orderBy("id").last(),
			db.scans.orderBy("id").last(),
			db.revocations.orderBy("ticket_id").last(),
			db.pass_scans.orderBy("id").last(),
		]);
		return dbResponse(DB_RESPONSE_OK, {
			lastTicketId: ticket?.id ?? 0,
			lastScanId: scan?.id ?? 0,
			lastRevocationId: revocation?.ticket_id ?? 0,
			lastPassScanId: passScan?.id ?? 0,
		} as LastSyncIds);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

/*
    For requesting necessary data to update tickets & scans to indexed db that need to be fetched from backend

*/
export async function requestTicketUpdateDataFromIndexedDb(): Promise<
	DBResponse<{
		lastScanned: number;
		lastTicket: number;
		lastRevocation: number;
		configuration: ScannerConfiguration;
		offlineTickets: OfflineTicket[];
	}>
> {
	try {
		const [lasts, configuration, offlineTickets] = await Promise.all([
			getLastIdsForTicketsApiCall(),
			db.singletons.get("configuration"),
			db.offlinetickets.toArray(),
		]);
		if (!isDbResponseOk(lasts)) {
			throw lasts;
		}
		if (!configuration) {
			throw new Error("No scanner config found");
		}
		return dbResponse(DB_RESPONSE_OK, {
			lastScanned: lasts.data.lastScanId,
			lastTicket: lasts.data.lastTicketId,
			lastRevocation: lasts.data.lastRevocationId ?? 0,
			configuration: configuration as ScannerConfiguration,
			offlineTickets,
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function requestLastScanIdFromIndexedDb(): Promise<DBResponse<number>> {
	try {
		const data = await db.singletons.get("lastSync");
		if (data) {
			return dbResponse(DB_RESPONSE_OK, (data as LastSyncIds).lastScanId);
		}
		return dbResponse(DB_RESPONSE_OK, 0);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getOfflineTicketsFromIndexedDb(): Promise<DBResponse<OfflineTicket[]>> {
	try {
		// Sort by scanned so the API receives them chronologically. The backend
		// assigns scan ids in receipt order and returns them sorted by id, so
		// upload order is what fixes the downstream entry/exit ordering.
		const offlineTickets = await db.offlinetickets.toArray();
		offlineTickets.sort((a, b) => (a.scanned < b.scanned ? -1 : a.scanned > b.scanned ? 1 : 0));
		return dbResponse(DB_RESPONSE_OK, offlineTickets);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function clearOfflineTicketsFromIndexedDb() {
	try {
		await db.offlinetickets.clear();
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function writeScansToIndexedDb(scans: InsertType<Scan, never>[] = []) {
	try {
		if (scans.length > 0) {
			await Promise.all([
				db.scans.bulkPut(scans),
				db.singletons.update("lastSync", { lastScanId: scans[scans.length - 1].id }).then(result => {
					if (result === 0) {
						return db.singletons.put(
							{ lastTicketId: 0, lastScanId: scans[scans.length - 1].id },
							"lastSync",
						);
					}
				}),
			]);
		}
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function writeRevocationsToIndexedDb(revocations: InsertType<Revocation, never>[] = []) {
	try {
		if (revocations.length > 0) {
			const lastId = revocations[revocations.length - 1].ticket_id;
			// Write the revocations and advance the cursor atomically: if bulkPut fails, the
			// transaction rolls back so lastRevocationId is not advanced past unsaved rows.
			await db.transaction("rw", db.revocations, db.singletons, async () => {
				await db.revocations.bulkPut(revocations);
				const result = await db.singletons.update("lastSync", { lastRevocationId: lastId });
				if (result === 0) {
					await db.singletons.put(
						{ lastTicketId: 0, lastScanId: 0, lastRevocationId: lastId },
						"lastSync",
					);
				}
			});
		}
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getAllBoothScansByDeviceLabelFromIndexedDb(device_label: string) {
	try {
		const scans = await db.scans.where("device_label").equals(device_label).toArray();
		return dbResponse(DB_RESPONSE_OK, scans);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

// To request for amount of unique scans.
//Scans amount can be greater that the ticket amount, since 1 ticket can be scanned multiple times
// export async function getUniqueScannedTicketsAmount() {
// 	try {
// 		const scans = await db.scans.toArray();
// 		const uniqueScans = [...new Set([...scans].map(s => s.ticket_id))].length;
// 		return dbResponse(DB_RESPONSE_OK, uniqueScans);
// 	} catch (err) {
// 		console.error(err);
// 		return dbResponse(DB_RESPONSE_ERROR);
// 	}
// }

// export async function getTicketScanCurrentState() {
// 	try {
// 		const scans = await db.scans.toArray();
// 		const uniqueScans = [...new Set([...scans].map(s => s.ticket_id))].length;
// 		const ticketsAmount = await db.tickets.count();

// 		return dbResponse(DB_RESPONSE_OK, {
// 			tickets: ticketsAmount,
// 			scans: uniqueScans,
// 		});
// 	} catch (err) {
// 		console.error(err);
// 		return dbResponse(DB_RESPONSE_ERROR);
// 	}
// }

export async function removeAllDataFromDatabase() {
	try {
		await db.delete();
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

// For persisting a configuration that is fetched from the backend to indexedDb.
export async function setConfigurationToDatabase({ configuration }: { configuration: { [key: string]: any } }) {
	try {
		await db.transaction("rw", db.singletons, db.events, db.products, async () => {
			let promises: Promise<any>[] = [];
			// Top-level `sections` carries new ScannerSection rows (one set across all
			// events). When empty, fall back to legacy `event_data.<slug>.venuemap.sections`
			// so legacy VenueSection-based configs continue to work after the seatsio
			// integration moved the canonical model.
			type ConfigSection = { id: number; name: string; access_control: number | null };
			const hasVenueSections = (e: unknown): e is { venuemap: { sections: ConfigSection[] } } =>
				!!e &&
				typeof e === "object" &&
				"venuemap" in e &&
				!!(e as { venuemap?: { sections?: unknown } }).venuemap &&
				Array.isArray((e as { venuemap: { sections?: unknown } }).venuemap.sections);
			const apexSections: ConfigSection[] = configuration.sections || [];
			const legacySections: ConfigSection[] = Object.values(configuration.event_data || {})
				.filter(hasVenueSections)
				.flatMap(e => e.venuemap.sections);
			const mergedSections = apexSections.length > 0 ? apexSections : legacySections;
			const config: InsertType<ScannerConfiguration, never> = {
				apikey: configuration.apikey,
				title: configuration.title,
				expiration_time: configuration.expiration_time,
				events: configuration.events,
				sections: mergedSections,
			};
			promises.push(db.singletons.put(config, "configuration"));

			if (configuration.booth_config) {
				const booth_config: InsertType<BoothConfig, never> = {
					...configuration.booth_config,
					apikey: configuration.apikey,
				};
				promises.push(db.singletons.put(booth_config, "booth"));
			}

			const event_config: InsertType<Event, never>[] = Object.keys(configuration.event_data || {}).map(slug => ({
				...configuration.event_data[slug],
				slug: slug,
			}));
			promises.push(db.events.bulkPut(event_config));

			const product_config: InsertType<Product, never>[] = [];
			Object.keys(configuration.event_data || {}).forEach(slug => {
				const eventProducts = configuration.event_data[slug].products || [];
				eventProducts.forEach(
					(product: {
						variants: typeof product_config;
						id: number;
						name: string;
						valid_from: string;
						valid_until: string;
					}) => {
						product.variants.forEach(variant => {
							product_config.push({
								...variant,
								id: variant.id,
								event_slug: slug,
								product_category: product.id,
								product_category_name: product.name,
								valid_from: product.valid_from,
								valid_until: product.valid_until,
							});
						});
					},
				);
			});
			promises.push(db.products.bulkPut(product_config));

			return await Promise.all(promises);
		});

		const [scannerConfigurationData, events, boothOptions] = await Promise.all([
			db.singletons.get("configuration"),
			db.events.toArray(),
			db.singletons.get("booth"),
		]);
		return dbResponse(DB_RESPONSE_OK, {
			configuration: scannerConfigurationData as ScannerConfiguration,
			events: events,
			boothOptions: boothOptions as BoothConfig,
		});
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

// Returns the current configuration set in the scanner
export async function getScannerConfigurationFromDatabase(): Promise<DBResponse<ScannerConfiguration>> {
	try {
		const cfg = await db.singletons.get("configuration");
		if (cfg) {
			return dbResponse(DB_RESPONSE_OK, cfg as ScannerConfiguration);
		}
		return dbResponse(DB_RESPONSE_ERROR, "No configuration found");
	} catch (err) {
		return dbResponse(200, err);
	}
}

export async function getScanModeOptionsFromIndexedDb(
	initialValue: typeof initialSettings.initialScanMode,
): Promise<DBResponse<ScanMode>> {
	try {
		const data = await db.singletons.get("scanMode");
		if (!data) {
			await db.singletons.put(initialValue, "scanMode");
			return dbResponse(DB_RESPONSE_OK, initialValue as ScanMode);
		}

		return dbResponse(DB_RESPONSE_OK, data as ScanMode);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function clearIndexedDbTable(table: string): Promise<DBResponse<void>> {
	try {
		await db.table(table).clear();
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function repopulateScansAndTicketsInIndexedDB(
	tickets: InsertType<Ticket, never>[] = [],
	scans: InsertType<Scan, never>[] = [],
	addScans = false,
): Promise<DBResponse<void>> {
	try {
		await db.tickets.clear();
		await db.tickets.bulkPut(tickets);
		if (addScans) {
			await db.scans.clear();
			await db.scans.bulkPut(scans);
		}
		// A full refetch reloads tickets as the current valid set, so any previously-revoked
		// ticket is already gone from `tickets`. Clear the revocation store and reset its cursor
		// so it rebuilds from `after=0` on the next sync — this recovers the refunded->paid
		// (un-revoke) case without ever leaving a stale flag.
		await db.revocations.clear();
		await db.singletons.put(
			{
				lastTicketId: tickets.length > 0 ? tickets[tickets.length - 1].id : 0,
				lastScanId: addScans && scans.length > 0 ? scans[scans.length - 1].id : 0,
				lastRevocationId: 0,
			},
			"lastSync",
		);
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function checkForNewTicketsAndScans(
	tickets = 0,
	scans = 0,
	checkScans = false,
): Promise<DBResponse<{ newTickets: number; newScans: number }>> {
	try {
		const ticketCount = await db.tickets.count();
		const newTickets = tickets - ticketCount;
		let newScans = 0;
		if (checkScans) {
			const scanCount = await db.scans.count();
			newScans = scans - scanCount;
		}
		return dbResponse(DB_RESPONSE_OK, { newTickets, newScans });
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

/* ------------------------- Season passes ------------------------- */

export async function replaceSeasonPassesInIndexedDb(
	passes: InsertType<SeasonPass, never>[] = [],
): Promise<DBResponse<{ count: number }>> {
	// Full snapshot replace on purpose: status flips (deactivation, renewal,
	// cancellation) must propagate, and pass volumes are small.
	try {
		await db.transaction("rw", db.season_passes, async () => {
			await db.season_passes.clear();
			await db.season_passes.bulkPut(passes);
		});
		return dbResponse(DB_RESPONSE_OK, { count: passes.length });
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function writePassScansToIndexedDb(scans: InsertType<PassScan, never>[] = []) {
	try {
		if (scans.length > 0) {
			const lastId = scans[scans.length - 1].id;
			await db.transaction("rw", db.pass_scans, db.singletons, async () => {
				await db.pass_scans.bulkPut(scans);
				const result = await db.singletons.update("lastSync", { lastPassScanId: lastId });
				if (result === 0) {
					await db.singletons.put(
						{ lastTicketId: 0, lastScanId: 0, lastRevocationId: 0, lastPassScanId: lastId },
						"lastSync",
					);
				}
			});
		}
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function writeOfflinePassScanToDatabase(
	scan: Omit<InsertType<OfflinePassScan, never>, "id">,
): Promise<DBResponse<void>> {
	try {
		await db.offline_pass_scans.add(scan as OfflinePassScan);
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getOfflinePassScansFromIndexedDb(): Promise<DBResponse<OfflinePassScan[]>> {
	try {
		const scans = await db.offline_pass_scans.toArray();
		scans.sort((a, b) => (a.scanned < b.scanned ? -1 : a.scanned > b.scanned ? 1 : 0));
		return dbResponse(DB_RESPONSE_OK, scans);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function clearOfflinePassScansFromIndexedDb() {
	try {
		await db.offline_pass_scans.clear();
		return dbResponse(DB_RESPONSE_OK);
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}

export async function getPassValidationDataFromDatabase(code: string): Promise<
	DBResponse<{
		pass: SeasonPass | null;
		todaysScans: (PassScan | OfflinePassScan)[];
		match: MatchEvent | null;
	}>
> {
	try {
		// The scanned string can be the SPS code or, for orderline-backed
		// passes, the normal ticket QR (tickets don't print the SPS code yet).
		let passRows = await db.season_passes.where("code").equals(code).toArray();
		if (passRows.length === 0) {
			passRows = await db.season_passes.where("ticket_code").equals(code).toArray();
		}
		if (passRows.length === 0) {
			return dbResponse(DB_RESPONSE_OK, { pass: null, todaysScans: [], match: null });
		}
		// Renewal rows share the code; exactly one row per code is ACTIVE.
		// Prefer it, else the newest row (so a fully-renewed chain reports
		// "renewed", not some older season's status). Load-bearing rule.
		const pass =
			passRows.find(p => p.status === "active") ??
			passRows.reduce((a, b) => (a.id > b.id ? a : b));

		const [synced, offline, events] = await Promise.all([
			db.pass_scans.where("season_pass_id").equals(pass.id).toArray(),
			db.offline_pass_scans.where("season_pass_id").equals(pass.id).toArray(),
			db.events.toArray(), // tiny table: the config's events
		]);
		const allScans = [...synced, ...offline];

		// Per-match rule when the season's matches are in the config:
		// attribute "now" to a match and count only scans of that match.
		const matchEvents = events.filter(e => e.season === pass.event_slug);
		const match = attributeMatch(matchEvents);
		if (match) {
			const scansForMatch = allScans.filter(s => {
				const synced_slug = (s as PassScan).match_slug;
				// Server attribution is authoritative when present; local
				// attribution (same rule, same window impl) covers offline rows.
				if (synced_slug !== undefined && synced_slug !== null) {
					return synced_slug === match.slug;
				}
				return attributeMatch(matchEvents, new Date(s.scanned))?.slug === match.slug;
			});
			return dbResponse(DB_RESPONSE_OK, { pass, todaysScans: scansForMatch, match });
		}

		// Fallback: no attributable match (no calendar, or outside windows).
		// ponytail: device-local day + full-history filter in JS; switch to
		// event-tz + indexed range if pass scan volumes ever hurt.
		const today = new Date().toDateString();
		const todaysScans = allScans.filter(s => new Date(s.scanned).toDateString() === today);
		return dbResponse(DB_RESPONSE_OK, { pass, todaysScans, match: null });
	} catch (err) {
		return dbResponse(DB_RESPONSE_ERROR, err);
	}
}
